/**
 * EmployerDetail — deep view with tabs: Overview · Roles · Timeline · Notes · Comms · Documents
 * Roles is the star — structured job records with their own matching + shortlist view.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, Phone, Globe, Plus, X, Trash2, Sparkles,
  Briefcase, Clock, StickyNote, MessageSquare, FileText, Users, ArrowRight, Save,
  Send, Eye, BarChart3,
} from "lucide-react";
import {
  BASE, ROLE_STATUSES, RoleStatusPill, StagePill, EmployerStatusPill, EMPLOYER_STATUSES,
  SectionHeader, EmptyState, MiniStat, authHeader, cx, empty, csvList, relTime,
} from "./shared";
import { TimelinePanel, NotesPanel, CommsPanel, DocumentsPanel, ConversationsPanel } from "./CandidateDetail";
import { TasksSection } from "./TasksSection";
import { EmployerBoardActivityPanel } from "@/components/shortlist/ShortlistActivityPanel";
import { PdfAttachmentField } from "./PdfAttachmentField";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TABS = [
  { k: "overview",  label: "Overview",  Icon: Sparkles },
  { k: "terms",     label: "Terms & notes", Icon: StickyNote },
  { k: "roles",     label: "Roles",     Icon: Briefcase },
  { k: "submissions", label: "Candidates Sent", Icon: Send },
  { k: "board_activity", label: "Candidate Board Activity", Icon: BarChart3 },
  { k: "conversations", label: "Conversations", Icon: Mail },
  { k: "timeline",  label: "Timeline",  Icon: Clock },
  { k: "notes",     label: "Notes",     Icon: StickyNote },
  { k: "comms",     label: "Comms",     Icon: MessageSquare },
  { k: "documents", label: "Documents", Icon: FileText },
];

export default function EmployerDetail({ employerId, pw, onBack, onRequestEdit, currentUser, reloadKey = 0 }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [roles, setRoles] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [notes, setNotes] = useState([]);
  const [comms, setComms] = useState([]);
  const [activeRoleId, setActiveRoleId] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);

  // Reply-now handoff: MyDayPanel stashes a draft under this key before
  // navigating here, so we can pop the send dialog with the reply pre-loaded.
  useEffect(() => {
    if (!employerId) return;
    const key = `ditch_reply_draft_${employerId}`;
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const draft = JSON.parse(raw);
        setPendingDraft(draft);
        setSendOpen(true);
        sessionStorage.removeItem(key);
      }
    } catch (err) { console.warn("EmployerDetail: could not restore pending draft", err); }
  }, [employerId]);

  const H = useMemo(() => authHeader(pw), [pw]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/employers/${employerId}`, H);
      setRecord(r.data);
    } catch {
      toast.error("Couldn't load employer.");
    } finally { setLoading(false); }
  }, [employerId, H]);

  const loadRoles = useCallback(async () => {
    const r = await axios.get(`${BASE}/roles?employer_id=${employerId}`, H);
    setRoles(r.data.roles || []);
  }, [employerId, H]);
  const loadTimeline = useCallback(async () => {
    const r = await axios.get(`${BASE}/timeline?entity_type=employer&entity_id=${employerId}`, H);
    setTimeline(r.data.items || []);
  }, [employerId, H]);
  const loadNotes = useCallback(async () => {
    const r = await axios.get(`${BASE}/notes?entity_type=employer&entity_id=${employerId}`, H);
    setNotes(r.data.notes || []);
  }, [employerId, H]);
  const loadComms = useCallback(async () => {
    const r = await axios.get(`${BASE}/comms?entity_type=employer&entity_id=${employerId}`, H);
    setComms(r.data.comms || []);
  }, [employerId, H]);

  useEffect(() => { load(); loadRoles(); }, [load, loadRoles]);
  useEffect(() => { if (reloadKey) load(); }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "timeline") loadTimeline();
    if (tab === "notes")    loadNotes();
    if (tab === "comms")    loadComms();
    if (tab === "roles")    loadRoles();
  }, [tab, loadTimeline, loadNotes, loadComms, loadRoles]);

  if (loading || !record) {
    return <p className="text-center text-[#6F8FA8] py-20">Loading employer…</p>;
  }

  const { employer: e, company: c, contact: p } = record;

  // Deep-drill into an individual role
  if (activeRoleId) {
    return (
      <RoleDetail
        roleId={activeRoleId}
        pw={pw}
        onBack={() => { setActiveRoleId(null); loadRoles(); }}
      />
    );
  }

  return (
    <div data-testid="employer-detail" className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-[#E4E7EA] overflow-hidden">
        <div className="p-6 border-b border-[#F2F2EF]">
          <div className="flex items-center justify-between mb-4">
            <button
              data-testid="employer-detail-back"
              onClick={onBack}
              className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] hover:text-[#0D2F4D] inline-flex items-center gap-1"
            >
              <ArrowLeft size={12} /> Back to employers
            </button>
            <button
              data-testid="employer-delete-profile"
              onClick={async () => {
                const label = c?.name || "this employer";
                const purgeCompany = window.confirm(
                  `DELETE employer profile for ${label}?\n\n` +
                  `This removes the employer's:\n· Roles + candidate applications\n· Notes + call/meeting logs\n· Timeline history\n· Documents + terms\n\n` +
                  `Click OK to also remove the company row entirely.\nClick Cancel to keep the company (in case other people belong to it).`
                );
                const confirmed = window.confirm("Are you absolutely sure? This can't be undone.");
                if (!confirmed) return;
                try {
                  await axios.delete(`${BASE}/employers/${employerId}?purge_company=${purgeCompany ? "true" : "false"}`, authHeader(pw));
                  toast.success("Employer profile deleted.");
                  onBack?.();
                } catch (err) {
                  toast.error(`Delete failed: ${err?.response?.data?.detail || err.message}`);
                }
              }}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-red-600 hover:text-white hover:bg-red-600 border border-red-200 rounded-full px-3 py-1.5 transition-colors"
            >
              <Trash2 size={12}/> Delete profile
            </button>
          </div>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0D2F4D] leading-tight">
                {c?.name || "Unknown"}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-[#6F8FA8]">
                {c?.domain && <span className="inline-flex items-center gap-1"><Globe size={13} /> {c.domain}</span>}
                {p?.email && <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 hover:text-[#0D2F4D]"><Mail size={13} /> {p.email}</a>}
                {p?.phone && <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 hover:text-[#0D2F4D]"><Phone size={13} /> {p.phone}</a>}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3 items-center">
                {e.recruitment_status && <EmployerStatusPill status={e.recruitment_status} />}
                {(c?.tags || []).map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#F2F2EF] text-[#0D2F4D] border border-[#E4E7EA]">
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="employer-send-branded-email"
                onClick={() => setSendOpen(true)}
                className="text-sm font-bold px-4 py-2 rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90 inline-flex items-center gap-1.5"
                title="Send a branded outreach email from a saved template"
              >
                <Send size={14}/>Send Branded Email
              </button>
              <button
                data-testid="employer-detail-edit"
                onClick={onRequestEdit}
                className="text-sm font-bold px-4 py-2 rounded-full bg-white text-[#0D2F4D] border border-[#D6DCE3] hover:border-[#0D2F4D]"
              >
                Edit details
              </button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 divide-x divide-[#F2F2EF]">
            <MiniStat label="Area" value={e.area || "—"} />
            <MiniStat label="Assigned to" value={e.assigned_to ? e.assigned_to.split("@")[0] : "unassigned"} />
            <MiniStat label="Source" value={e.source || "—"} />
            <MiniStat label="Open roles" value={roles.filter((r) => ["open", "shortlisting"].includes(r.status)).length} />
          </div>

          {/* Hunter cadence chip */}
          <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="employer-followup-bar">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8]">Next follow-up</span>
            {(() => {
              const fu = e.next_follow_up_at;
              if (!fu) return <span className="text-xs text-[#94A3B8] italic">not scheduled</span>;
              const due = new Date(fu.length > 10 ? fu : fu + "T00:00:00Z");
              const now = new Date();
              const diff = Math.floor((due - now) / 86400000);
              const overdue = diff < 0;
              const label = overdue ? `${Math.abs(diff)}d overdue` : diff === 0 ? "today" : `in ${diff}d`;
              return (
                <span data-testid="employer-followup-chip" className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold ${overdue ? "bg-red-100 text-red-700 border border-red-300" : diff === 0 ? "bg-[#C6D21F] text-[#0D2F4D]" : "bg-[#F7F9E8] text-[#0D2F4D] border border-[#C6D21F]/40"}`}>
                  {due.toISOString().slice(0,10)} · {label}
                </span>
              );
            })()}
            <input
              type="date"
              value={e.next_follow_up_at ? e.next_follow_up_at.slice(0, 10) : ""}
              data-testid="employer-followup-input"
              onChange={async (ev) => {
                const v = ev.target.value;
                try {
                  await axios.patch(`${BASE}/employers/${employerId}`, { next_follow_up_at: v || "" }, authHeader(pw));
                  toast.success(v ? `Follow-up set: ${v}` : "Follow-up cleared");
                  load();
                } catch (err) { toast.error("Save failed"); }
              }}
              className="text-xs px-2 py-1 rounded border border-[#D6DCE3] focus:border-[#0D2F4D] outline-none"
            />
            {["1d","3d","7d","14d"].map(d => (
              <button
                key={d}
                data-testid={`employer-snooze-${d}`}
                onClick={async () => {
                  const n = parseInt(d);
                  const dt = new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
                  try {
                    await axios.patch(`${BASE}/employers/${employerId}`, { next_follow_up_at: dt }, authHeader(pw));
                    toast.success(`Snoozed ${d} → ${dt}`);
                    load();
                  } catch (err) { toast.error("Snooze failed"); }
                }}
                className="text-[10px] font-bold px-2 py-1 rounded-full bg-white text-[#0D2F4D] border border-[#D6DCE3] hover:border-[#0D2F4D] hover:bg-[#F7F9E8]"
              >+{d}</button>
            ))}
          </div>
        </div>

        <div className="border-b border-[#F2F2EF] flex flex-wrap px-2">
          {TABS.map(({ k, label, Icon }) => (
            <button
              key={k}
              data-testid={`employer-tab-${k}`}
              onClick={() => setTab(k)}
              className={cx(
                "px-4 py-3 text-xs font-bold inline-flex items-center gap-1.5 border-b-2 transition-colors",
                tab === k
                  ? "border-[#C6D21F] text-[#0D2F4D]"
                  : "border-transparent text-[#6F8FA8] hover:text-[#0D2F4D]"
              )}
            >
              <Icon size={13} /> {label}
              {k === "roles" && roles.length > 0 && (
                <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#F2F2EF] text-[#6F8FA8]">
                  {roles.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E4E7EA] p-6" data-testid={`employer-tabpanel-${tab}`}>
        {tab === "overview" && (
          <>
            <EmployerOverview e={e} p={p} />
            <div className="mt-4">
              <TasksSection pw={pw} user={currentUser} entityType="employer" entityId={employerId} />
            </div>
          </>
        )}
        {tab === "terms"    && <EmployerTermsPanel employerId={employerId} pw={pw} record={record} onChange={load} />}
        {tab === "roles"    && (
          <RolesPanel
            roles={roles}
            employerId={employerId}
            pw={pw}
            onChange={loadRoles}
            onOpen={setActiveRoleId}
          />
        )}
        {tab === "conversations" && <ConversationsPanel entityType="employer" entityId={employerId} pw={pw} />}
        {tab === "timeline" && <TimelinePanel items={timeline} pw={pw} onChange={loadTimeline} />}
        {tab === "notes"    && <NotesPanel entityId={employerId} entityType="employer" pw={pw} items={notes} onChange={loadNotes} />}
        {tab === "comms"    && <CommsPanel entityId={employerId} entityType="employer" pw={pw} items={comms} onChange={loadComms} />}
        {tab === "documents"&& <DocumentsPanel docs={e.documents || []} entityId={employerId} entityType="employer" pw={pw} onChange={load} />}
        {tab === "submissions" && <SubmissionsPanel employerId={employerId} pw={pw} />}
        {tab === "board_activity" && <EmployerBoardActivityPanel activity={e.candidate_board_activity || {}} employerId={employerId} />}
      </div>

      <SendBrandedEmailDialog
        open={sendOpen}
        onClose={() => { setSendOpen(false); setPendingDraft(null); }}
        employerId={employerId}
        contact={p}
        company={c}
        pw={pw}
        currentUser={currentUser}
        initialSubject={pendingDraft?.subject}
        initialBody={pendingDraft?.body}
        onSent={() => { setSendOpen(false); setPendingDraft(null); loadComms(); loadTimeline(); }}
      />
    </div>
  );
}

function SendBrandedEmailDialog({ open, onClose, employerId, contact, company, pw, onSent, initialSubject, initialBody, currentUser }) {
  const [templates, setTemplates] = useState([]);
  const [tid, setTid] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [senders, setSenders] = useState([]);
  const [selectedSender, setSelectedSender] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [attachIds, setAttachIds] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const H = useMemo(() => authHeader(pw), [pw]);

  useEffect(() => {
    if (!open) return;
    setSubject(initialSubject || "");
    setBody(initialBody || "");
    setAttachIds([]);
    setAttachment(null);
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/outreach/custom-templates`, H)
      .then(r => setTemplates(r.data.templates || []))
      .catch(() => setTemplates([]));
    // Candidate roster for one-click profile PDF attachments.
    axios.get(`${BASE}/candidates?limit=300`, H)
      .then(r => setCandidates(r.data.candidates || []))
      .catch(() => setCandidates([]));
    // Load the team roster + per-admin Gmail status so the user can pick who signs the email.
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/senders`, H)
      .then(r => {
        const list = r.data.senders || [];
        setSenders(list);
        // Default to the signed-in user when we know them, else the first row.
        const meEmail = (currentUser?.email || "").toLowerCase();
        const me = list.find(s => s.email === meEmail);
        setSelectedSender(me?.email || list[0]?.email || "");
      })
      .catch(() => setSenders([]));
  }, [open, H, initialSubject, initialBody, currentUser?.email]);

  const senderRow = senders.find(s => s.email === selectedSender) || null;
  const discardAndClose = async () => {
    if (attachment?.id) {
      try { await axios.delete(`${BASE}/email-attachments/${attachment.id}`, { headers: { "X-Admin-Password": pw } }); } catch (_) {}
    }
    setAttachment(null); onClose();
  };

  const pickTemplate = (id) => {
    setTid(id);
    const t = templates.find(x => x.id === id);
    if (t) {
      setSubject(t.subject || "");
      setBody(t.body || "");
      setTemplateName(t.name || "");
    }
  };

  const doPreview = async () => {
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/outreach/preview`,
        { subject, body, brand: "careers", context: {
            first_name: contact?.first_name || "there",
            last_name: contact?.last_name || "",
            company: company?.name || "",
            industry: "",
            email: contact?.email,
        } },
        H,
      );
      setPreviewHtml(r.data.html || "");
      setPreviewOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };

  const doSend = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body required."); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${BASE}/employers/${employerId}/send-branded-email`,
        {
          subject, body,
          template_id: tid || null,
          template_name: templateName || null,
          brand: "careers",
          sender_admin_email: selectedSender || null,
          attach_candidate_ids: attachIds,
          attachment_id: attachment?.id || null,
        },
        H,
      );
      if (r.data.ok) {
        const via = r.data?.resend?.provider === "gmail" ? "Gmail" : "Resend";
        const attached = (r.data?.attached_profiles || []).length;
        const documentName = r.data?.attached_document?.filename;
        setAttachment(null);
        toast.success(`Email sent via ${via}${attached ? ` with ${attached} profile PDF${attached > 1 ? "s" : ""}` : ""}${documentName ? ` + ${documentName}` : ""} + logged to timeline.`);
        onSent?.();
      } else {
        toast.error(`Send failed: ${r.data?.resend?.error || "unknown"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={o => !o && discardAndClose()}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="employer-send-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold text-[#0D2F4D]">
              Send branded email to {contact?.first_name || contact?.email || "contact"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Signing as</label>
              <Select value={selectedSender || "__none__"} onValueChange={(v) => setSelectedSender(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="employer-send-sender">
                  <SelectValue placeholder="Signed-in user"/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Team desk (shared)</SelectItem>
                  {senders.map(s => (
                    <SelectItem key={s.email} value={s.email} data-testid={`sender-option-${s.email}`}>
                      {s.name} · {s.email} {s.gmail_connected ? "· Gmail ✓" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSender && senderRow && (
                <p className="text-[11px] text-[#6F8FA8] mt-1" data-testid="employer-send-sender-status">
                  {senderRow.gmail_connected
                    ? <>Sending via <strong className="text-emerald-700">Gmail</strong> ({senderRow.gmail_email}) — lands in {senderRow.name}'s Sent folder. Reply-To: {senderRow.email}.</>
                    : <>Sending via <strong>Resend</strong> — From: "{senderRow.name} · The Ditch Careers". Reply-To: {senderRow.email}. Connect Gmail on Integrations for better deliverability.</>}
                </p>
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Load a custom template (optional)</label>
              <Select value={tid || "__none__"} onValueChange={(v) => v === "__none__" ? setTid("") : pickTemplate(v)}>
                <SelectTrigger data-testid="employer-send-tpl"><SelectValue placeholder={templates.length ? "Pick a template…" : "No templates yet — write below"}/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Write from scratch</SelectItem>
                  {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Subject</label>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                data-testid="employer-send-subject"
                placeholder="Hey {{first_name}} — quick note about hiring at {{company}}"
                className="w-full text-sm px-3 py-2 rounded-md border border-[#D6DCE3] focus:border-[#0D2F4D] focus:ring-1 focus:ring-[#0D2F4D] outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Body (Markdown OK)</label>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                data-testid="employer-send-body"
                rows={10}
                placeholder="Hi {{first_name}},&#10;&#10;Saw you were hiring in **{{industry}}** — worth a 10 min chat?&#10;&#10;Laura"
                className="w-full text-sm px-3 py-2 rounded-md border border-[#D6DCE3] focus:border-[#0D2F4D] focus:ring-1 focus:ring-[#0D2F4D] outline-none font-mono"
              />
              <p className="text-[10px] text-[#6F8FA8] mt-1">Tokens: {"{{first_name}}"} · {"{{last_name}}"} · {"{{company}}"} · {"{{industry}}"}. Renders through the Careers brand shell.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Attach candidate profile (PDF) — optional, max 3</label>
              <Select value="__pick__" onValueChange={(v) => {
                if (v === "__pick__") return;
                setAttachIds(prev => prev.includes(v) || prev.length >= 3 ? prev : [...prev, v]);
              }}>
                <SelectTrigger data-testid="employer-send-attach-picker">
                  <SelectValue placeholder="Pick a candidate…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__pick__">Pick a candidate…</SelectItem>
                  {candidates.map(c => (
                    <SelectItem key={c.id} value={c.id} data-testid={`attach-option-${c.id}`}>
                      {(c.person?.first_name || "?") + " " + (c.person?.last_name || "")} {c.role_focus || c.headline ? `· ${c.role_focus || c.headline}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {attachIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="employer-send-attach-chips">
                  {attachIds.map(id => {
                    const c = candidates.find(x => x.id === id);
                    const nm = c ? `${c.person?.first_name || "?"} ${c.person?.last_name || ""}`.trim() : id.slice(0, 8);
                    return (
                      <span key={id} className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#0D2F4D] text-[#C6D21F]" data-testid={`attach-chip-${id}`}>
                        {nm} — Profile.pdf
                        <button onClick={() => setAttachIds(prev => prev.filter(x => x !== id))} className="hover:text-white" data-testid={`attach-remove-${id}`} title="Remove attachment">✕</button>
                      </span>
                    );
                  })}
                </div>
              )}
              <p className="text-[10px] text-[#6F8FA8] mt-1">Attaches the Ditch Careers Candidate Profile PDF. Attachment emails send via Resend (Gmail routing doesn't carry attachments).</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Attach a PDF document — optional</label>
              <PdfAttachmentField value={attachment} onChange={setAttachment} pw={pw} testIdPrefix="employer-send"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={discardAndClose} data-testid="employer-send-cancel">Cancel</Button>
            <Button variant="outline" onClick={doPreview} disabled={!subject || !body} data-testid="employer-send-preview"><Eye className="h-4 w-4 mr-1"/>Preview</Button>
            <Button
              className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]"
              onClick={doSend}
              disabled={busy || !subject || !body}
              data-testid="employer-send-submit"
            ><Send className="h-4 w-4 mr-1"/>{busy ? "Sending…" : "Send email"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={previewOpen} onOpenChange={o => !o && setPreviewOpen(false)}>
        <DialogContent className="max-w-4xl h-[92vh] flex flex-col p-0" data-testid="employer-send-preview-dialog">
          <DialogHeader className="px-6 py-4 border-b border-[#E4E7EA] shrink-0">
            <DialogTitle className="flex items-center gap-2 text-[#0D2F4D]">
              <Eye className="h-4 w-4"/>Branded preview — Careers
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-[#F2F2EF] px-4 py-4">
            <iframe title="preview" srcDoc={previewHtml} className="w-full h-full bg-white rounded-lg shadow-sm border border-[#E4E7EA]" sandbox="" data-testid="employer-send-preview-iframe"/>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EmployerOverview({ e, p }) {
  const rows = [
    ["Contact", `${p?.first_name || ""} ${p?.last_name || ""}`.trim()],
    ["Contact email", p?.email],
    ["Contact phone", p?.phone],
    ["Industry", e.industry],
    ["Location", e.location],
    ["Head office", e.head_office],
    ["Company size", e.company_size],
    ["ABN", e.abn],
    ["Hiring frequency", e.hiring_frequency],
    ["Recruitment fee", e.recruitment_fee || e.hiring_fee],
    ["Active", e.active ? "Yes" : "No"],
  ];
  // Hide blank rows unless they're always-render (Industry / Contact etc.).
  const alwaysShow = new Set(["Contact", "Contact email", "Industry", "Location", "Active"]);
  const visible = rows.filter(([label, v]) => alwaysShow.has(label) || !empty(v));
  return (
    <div className="space-y-6">
      <div>
        <SectionHeader title="Details" />
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {visible.map(([label, v]) => (
            <div key={label} className="flex gap-2" data-testid={`employer-field-${label.toLowerCase().replaceAll(" ", "-")}`}>
              <span className="text-[#6F8FA8] text-[11px] uppercase tracking-widest font-bold whitespace-nowrap w-32 md:w-40">{label}</span>
              <span className="text-[#0D2F4D]">{empty(v) ? "—" : String(v)}</span>
            </div>
          ))}
        </div>
      </div>
      {(e.hiring_for || []).length > 0 && (
        <div>
          <SectionHeader title="Hiring for (freeform)" />
          <div className="flex flex-wrap gap-1.5">
            {e.hiring_for.map((r) => (
              <span key={r} className="text-xs font-semibold px-2 py-1 rounded-lg bg-[#F2F2EF] text-[#0D2F4D]">{r}</span>
            ))}
          </div>
          <p className="text-[11px] text-[#94A3B8] mt-2">Use the Roles tab for structured records with status + matching.</p>
        </div>
      )}
      {e.notes && (
        <div>
          <SectionHeader title="Notes" />
          <p className="text-sm text-[#0D2F4D] whitespace-pre-line bg-[#FAFAF7] p-4 rounded-xl">{e.notes}</p>
        </div>
      )}

      {/* ── Discovery & onboarding — everything captured on the employer
          intake / brief that isn't already in Details above. Pulls through
          any dict field (brief_notes, discovery, hiring_needs, profile_all)
          plus a catch-all for miscellaneous keys. Blanks are hidden. */}
      {(() => {
        const shownKeys = new Set([
          "_id","id","company_id","contact_person_id","active","created_at","updated_at",
          "industry","location","head_office","company_size","abn","hiring_frequency",
          "recruitment_fee","hiring_fee","hiring_for","notes","tags","area","assigned_to",
          "source","owner","recruitment_status","last_touch_at","company_name",
          "contact_email","contact_first_name","contact_last_name","contact_phone",
          "company_domain","brand","brands","stripe_customer_id",
        ]);
        const richDicts = ["brief","brief_notes","discovery","discovery_notes",
          "hiring_needs","onboarding","profile_all"];
        const dictSections = richDicts
          .map((k) => [k, e[k]])
          .filter(([, v]) => v && typeof v === "object" && !Array.isArray(v) && Object.keys(v).length > 0);
        // Catch-all: any other top-level dict/string field we don't render.
        const extras = Object.fromEntries(
          Object.entries(e).filter(([k, v]) =>
            !shownKeys.has(k) && !richDicts.includes(k) &&
            v !== null && v !== undefined && v !== "" &&
            !(Array.isArray(v) && v.length === 0) &&
            typeof v !== "function",
          ),
        );
        if (dictSections.length === 0 && Object.keys(extras).length === 0) return null;
        return (
          <div data-testid="employer-discovery-block">
            <SectionHeader title="Discovery & onboarding" />
            <p className="text-[11px] text-[#94A3B8] mb-3">
              Everything from the employer intake / brief. Blanks are hidden.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {dictSections.map(([label, dict]) => (
                <div key={label} data-testid={`employer-dict-${label}`} className="bg-[#FAFAF7] rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] mb-1.5">
                    {label.replaceAll("_", " ")}
                  </p>
                  <EmployerDictSummary d={dict} />
                </div>
              ))}
              {Object.keys(extras).length > 0 && (
                <div data-testid="employer-extras" className="bg-[#FAFAF7] rounded-xl p-3 md:col-span-2">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] mb-1.5">
                    Other answers
                  </p>
                  <EmployerDictSummary d={extras} />
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// Employer-scope mini-list — same shape as candidate DictSummary but
// lives here so EmployerDetail doesn't import from CandidateDetail.
function EmployerDictSummary({ d }) {
  const entries = Object.entries(d || {}).filter(([, v]) =>
    v !== null && v !== undefined && v !== "" &&
    !(Array.isArray(v) && v.length === 0),
  );
  if (!entries.length) return <p className="text-xs text-[#6F8FA8]">—</p>;
  return (
    <dl className="text-xs text-[#0D2F4D] space-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-2">
          <dt className="text-[#6F8FA8] capitalize whitespace-nowrap">
            {k.replaceAll("_", " ")}
          </dt>
          <dd className="font-medium break-words">
            {Array.isArray(v)
              ? v.map((x) => (typeof x === "object" ? JSON.stringify(x) : String(x))).join(", ")
              : typeof v === "object" ? JSON.stringify(v) : String(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* ─────────────────────────── Roles panel ─────────────────────────── */
function RolesPanel({ roles, employerId, pw, onChange, onOpen }) {
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <div className="space-y-4">
      <SectionHeader
        title="Open roles"
        right={
          <button
            data-testid="role-create-btn"
            onClick={() => setModalOpen(true)}
            className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#0D2F4D] text-white hover:bg-[#1a4063] inline-flex items-center gap-1.5"
          >
            <Plus size={12} /> New role
          </button>
        }
      />
      {roles.length === 0 ? (
        <EmptyState text="No structured roles yet. Add one to unlock candidate matching." />
      ) : (
        <ul className="space-y-3">
          {roles.map((r) => (
            <li
              key={r.id}
              data-testid={`role-row-${r.id}`}
              className="bg-[#FAFAF7] rounded-xl p-4 flex items-start justify-between gap-3 hover:bg-[#F2F2EF] cursor-pointer"
              onClick={() => onOpen(r.id)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-[#0D2F4D]">{r.title}</p>
                  <RoleStatusPill status={r.status} />
                </div>
                <p className="text-xs text-[#6F8FA8] mt-1">
                  {r.seniority || "—"} · {r.location || "—"} · {r.salary_range || "—"} {r.remote_ok ? "· remote OK" : ""}
                </p>
                {(r.requirements || []).length > 0 && (
                  <p className="text-xs text-[#6F8FA8] mt-1 truncate">
                    <span className="font-semibold text-[#0D2F4D]">Req:</span> {r.requirements.join(", ")}
                  </p>
                )}
              </div>
              <ArrowRight size={14} className="text-[#94A3B8] shrink-0 mt-1" />
            </li>
          ))}
        </ul>
      )}
      {modalOpen && (
        <RoleModal
          employerId={employerId}
          pw={pw}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); onChange(); }}
        />
      )}
    </div>
  );
}

function RoleModal({ employerId, pw, onClose, onSaved, editing }) {
  const [f, setF] = useState(() => editing ? {
    title: editing.title || "", description: editing.description || "",
    seniority: editing.seniority || "", location: editing.location || "",
    salary_range: editing.salary_range || "", employment_type: editing.employment_type || "",
    remote_ok: !!editing.remote_ok, status: editing.status || "open",
    industry: editing.industry || "",
    requirements: (editing.requirements || []).join(", "),
    nice_to_haves: (editing.nice_to_haves || []).join(", "),
  } : {
    title: "", description: "", seniority: "", location: "",
    salary_range: "", employment_type: "", remote_ok: false, status: "open",
    industry: "", requirements: "", nice_to_haves: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.title.trim()) { toast.error("Role title is required."); return; }
    setSaving(true);
    try {
      const body = {
        ...f,
        requirements: csvList(f.requirements),
        nice_to_haves: csvList(f.nice_to_haves),
      };
      if (editing) {
        await axios.patch(`${BASE}/roles/${editing.id}`, body, authHeader(pw));
      } else {
        await axios.post(`${BASE}/roles`, { ...body, employer_id: employerId }, authHeader(pw));
      }
      toast.success(editing ? "Role updated." : "Role created.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save role.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-2xl w-full my-8 border border-[#E4E7EA]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E4E7EA]">
          <h3 className="font-display text-xl font-extrabold text-[#0D2F4D]">
            {editing ? "Edit role" : "New role"}
          </h3>
          <button onClick={onClose} className="text-[#6F8FA8] hover:text-[#0D2F4D]"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <input
            data-testid="role-form-title"
            value={f.title}
            onChange={(e) => setF({ ...f, title: e.target.value })}
            placeholder="Role title *"
            className="w-full rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm"
          />
          <textarea
            data-testid="role-form-description"
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
            placeholder="Role description / brief"
            rows={3}
            className="w-full rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm resize-y"
          />
          <div className="grid md:grid-cols-2 gap-3">
            <input value={f.seniority} onChange={(e) => setF({ ...f, seniority: e.target.value })} placeholder="Seniority (junior/mid/senior)" className="rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm" />
            <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Location" className="rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm" />
            <input value={f.salary_range} onChange={(e) => setF({ ...f, salary_range: e.target.value })} placeholder="Salary range" className="rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm" />
            <input value={f.employment_type} onChange={(e) => setF({ ...f, employment_type: e.target.value })} placeholder="Full-time / contract" className="rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm" />
            <input value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })} placeholder="Industry" className="rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm" />
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm">
              {ROLE_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#0D2F4D]">
            <input type="checkbox" checked={f.remote_ok} onChange={(e) => setF({ ...f, remote_ok: e.target.checked })} className="accent-[#0D2F4D]" />
            Remote OK
          </label>
          <input data-testid="role-form-requirements" value={f.requirements} onChange={(e) => setF({ ...f, requirements: e.target.value })} placeholder="Required skills (comma-separated)" className="w-full rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm" />
          <input value={f.nice_to_haves} onChange={(e) => setF({ ...f, nice_to_haves: e.target.value })} placeholder="Nice-to-haves (comma-separated)" className="w-full rounded-lg border border-[#D6DCE3] px-3 py-2 text-sm" />
        </div>
        <div className="px-6 py-4 border-t border-[#E4E7EA] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-[#6F8FA8] hover:text-[#0D2F4D]">Cancel</button>
          <button data-testid="role-form-submit" disabled={saving} onClick={submit} className="px-5 py-2 text-sm font-bold rounded-full bg-[#0D2F4D] text-white hover:bg-[#1a4063] disabled:opacity-50">
            {saving ? "Saving…" : editing ? "Save" : "Create role"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Role detail (drill in) ─────────────────────────── */
function RoleDetail({ roleId, pw, onBack }) {
  const [record, setRecord] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const H = useMemo(() => authHeader(pw), [pw]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/roles/${roleId}`, H);
      setRecord(r.data);
      const m = await axios.get(`${BASE}/roles/${roleId}/matches?limit=10`, H);
      setMatches(m.data.matches || []);
    } catch { toast.error("Couldn't load role."); }
    finally { setLoading(false); }
  }, [roleId, H]);

  useEffect(() => { load(); }, [load]);

  const apply = async (candidateId) => {
    try {
      await axios.post(`${BASE}/applications`, { candidate_id: candidateId, role_id: roleId }, H);
      toast.success("Shortlisted!");
      load();
    } catch { toast.error("Couldn't shortlist."); }
  };
  const removeApp = async (appId) => {
    try {
      await axios.delete(`${BASE}/applications/${appId}`, H);
      load();
    } catch { toast.error("Couldn't remove."); }
  };
  const removeRole = async () => {
    if (!window.confirm("Delete this role permanently?")) return;
    try {
      await axios.delete(`${BASE}/roles/${roleId}`, H);
      toast.success("Role deleted.");
      onBack();
    } catch { toast.error("Couldn't delete."); }
  };

  if (loading || !record) return <p className="text-center text-[#6F8FA8] py-20">Loading role…</p>;

  const { role: r, company, applications } = record;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#E4E7EA] p-6">
        <button
          data-testid="role-detail-back"
          onClick={onBack}
          className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] hover:text-[#0D2F4D] inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={12} /> Back to roles
        </button>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <RoleStatusPill status={r.status} />
              {r.remote_ok && <span className="text-[10px] font-bold uppercase tracking-widest text-[#22C55E]">Remote OK</span>}
            </div>
            <h1 className="font-display text-3xl font-extrabold text-[#0D2F4D]">{r.title}</h1>
            <p className="text-[#6F8FA8] mt-1">
              {company?.name || "—"} · {r.location || "—"} · {r.salary_range || "—"}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditOpen(true)} className="text-sm font-bold px-4 py-2 rounded-full bg-white text-[#0D2F4D] border border-[#D6DCE3] hover:border-[#0D2F4D]">Edit</button>
            <button onClick={removeRole} className="text-sm font-bold px-4 py-2 rounded-full bg-white text-red-500 border border-red-200 hover:border-red-500">Delete</button>
          </div>
        </div>

        {r.description && (
          <p className="mt-6 text-sm text-[#0D2F4D] whitespace-pre-line bg-[#FAFAF7] p-4 rounded-xl">{r.description}</p>
        )}

        {(r.requirements || []).length > 0 && (
          <div className="mt-6">
            <SectionHeader title="Required" />
            <div className="flex flex-wrap gap-1.5">
              {r.requirements.map((s) => (
                <span key={s} className="text-xs font-semibold px-2 py-1 rounded-lg bg-[#0D2F4D] text-white">{s}</span>
              ))}
            </div>
          </div>
        )}
        {(r.nice_to_haves || []).length > 0 && (
          <div className="mt-4">
            <SectionHeader title="Nice to have" />
            <div className="flex flex-wrap gap-1.5">
              {r.nice_to_haves.map((s) => (
                <span key={s} className="text-xs font-semibold px-2 py-1 rounded-lg bg-[#F2F2EF] text-[#0D2F4D]">{s}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Shortlist (applications) */}
      <div className="bg-white rounded-2xl border border-[#E4E7EA] p-6">
        <SectionHeader title="Shortlist" right={<span className="text-xs text-[#6F8FA8]">{applications.length} candidates</span>} />
        {applications.length === 0 ? (
          <EmptyState text="Nobody shortlisted yet — pick from ranked matches below." />
        ) : (
          <ul className="space-y-2">
            {applications.map((a) => (
              <li key={a.id} className="bg-[#FAFAF7] rounded-xl p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Users size={14} className="text-[#6F8FA8]" />
                  <div>
                    <p className="text-sm font-semibold text-[#0D2F4D]">
                      {a.candidate?.person?.first_name} {a.candidate?.person?.last_name}
                    </p>
                    <p className="text-xs text-[#6F8FA8]">{a.candidate?.person?.email} · {a.candidate?.industry || "—"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StagePill stage={a.candidate?.stage || "applied"} />
                  <button onClick={() => removeApp(a.id)} className="text-[#94A3B8] hover:text-red-500"><X size={14} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Ranked matches */}
      <div className="bg-white rounded-2xl border border-[#E4E7EA] p-6">
        <SectionHeader
          title="Ranked matches"
          right={<span className="text-xs text-[#6F8FA8]">based on industry, skills, location, seniority</span>}
        />
        {matches.length === 0 ? (
          <EmptyState text="No matching candidates in the pool yet." />
        ) : (
          <ul className="space-y-2">
            {matches.map((m) => (
              <li key={m.id} data-testid={`role-match-${m.id}`} className="bg-[#FAFAF7] rounded-xl p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center font-display font-extrabold text-white text-lg"
                    style={{ background: m.match.score >= 70 ? "#22C55E" : m.match.score >= 40 ? "#FBBF24" : "#94A3B8" }}
                  >
                    {m.match.score}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0D2F4D]">
                      {m.person?.first_name} {m.person?.last_name}
                    </p>
                    <p className="text-xs text-[#6F8FA8]">{m.person?.email} · {m.industry || "—"} · {m.location_target || m.location_now || "—"}</p>
                    <p className="text-[11px] text-[#22C55E] mt-0.5">{m.match.reasons.join(" · ")}</p>
                    {m.match.penalties.length > 0 && (
                      <p className="text-[11px] text-red-500 mt-0.5">{m.match.penalties.join(" · ")}</p>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`role-shortlist-${m.id}`}
                  onClick={() => apply(m.id)}
                  className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:brightness-95"
                >
                  Shortlist
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editOpen && (
        <RoleModal
          employerId={r.employer_id}
          pw={pw}
          editing={r}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────── Employer Terms & Notes ─────────────────────────────── */
function EmployerTermsPanel({ employerId, pw, record, onChange }) {
  const H = useMemo(() => authHeader(pw), [pw]);
  const e = record?.employer || {};
  const initialTerms = e.terms || {
    terms_signed: false,
    recruitment_fee: "",
    invoice_terms: "",
    recruiter_notes: "",
    last_contact: "",
    feedback: "",
    next_follow_up: "",
  };
  const [status, setStatus] = useState(e.recruitment_status || "prospect");
  const [terms, setTerms] = useState(initialTerms);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(e.recruitment_status || "prospect");
    setTerms(e.terms || initialTerms);
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  const setT = (patch) => { setTerms((t) => ({ ...t, ...patch })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${BASE}/employers/${employerId}`, {
        recruitment_status: status,
        terms,
      }, H);
      toast.success("Employer terms saved");
      setDirty(false);
      onChange?.();
    } catch (err) {
      toast.error(`Save failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="employer-terms-panel">
      <div className="flex items-center justify-between">
        <SectionHeader title="Recruitment status" />
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="employer-terms-save"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-full bg-[#0D2F4D] text-[#C6D21F] disabled:opacity-40 hover:bg-[#0D2F4D]/90 transition-colors"
        >
          <Save size={14} />{saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      {/* Status pill row */}
      <div className="flex flex-wrap gap-2" data-testid="employer-status-row">
        {EMPLOYER_STATUSES.map((s) => {
          const active = status === s.v;
          return (
            <button
              key={s.v}
              onClick={() => { setStatus(s.v); setDirty(true); }}
              data-testid={`employer-status-${s.v}`}
              className={cx(
                "px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest border transition-colors",
                active
                  ? "text-white"
                  : "hover:bg-[#F2F2EF]"
              )}
              style={active
                ? { background: s.color, borderColor: s.color, color: "#fff" }
                : { borderColor: s.color, color: s.color, background: `${s.color}12` }}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Terms grid */}
      <div className="border-t border-[#E4E7EA] pt-6 space-y-6">
        <SectionHeader title="Terms" />
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">Terms signed</label>
            <div className="mt-2">
              <label className="inline-flex items-center gap-2 text-sm text-[#0D2F4D]" data-testid="employer-terms-signed-row">
                <input
                  type="checkbox"
                  checked={!!terms.terms_signed}
                  onChange={(ev) => setT({ terms_signed: ev.target.checked })}
                  className="w-4 h-4 rounded"
                  data-testid="employer-terms-signed"
                />
                Signed & countersigned
              </label>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">Recruitment fee</label>
            <input
              value={terms.recruitment_fee || ""}
              onChange={(ev) => setT({ recruitment_fee: ev.target.value })}
              data-testid="employer-recruitment-fee"
              placeholder="e.g. 20% of first-year salary — pre-filled from Careers pricing page"
              className="mt-1.5 w-full border border-[#E4E7EA] rounded-xl p-2.5 text-sm focus:border-[#0D2F4D] outline-none"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">Invoice terms</label>
            <input
              value={terms.invoice_terms || ""}
              onChange={(ev) => setT({ invoice_terms: ev.target.value })}
              data-testid="employer-invoice-terms"
              placeholder="e.g. Net 14 days from placement start · 3-month replacement guarantee"
              className="mt-1.5 w-full border border-[#E4E7EA] rounded-xl p-2.5 text-sm focus:border-[#0D2F4D] outline-none"
            />
          </div>
        </div>
      </div>

      {/* Follow-up / notes */}
      <div className="border-t border-[#E4E7EA] pt-6 space-y-6">
        <SectionHeader title="Follow-up" />
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">Last contact</label>
            <input
              value={terms.last_contact || ""}
              onChange={(ev) => setT({ last_contact: ev.target.value })}
              data-testid="employer-last-contact"
              placeholder="2 days ago · or a specific date"
              className="mt-1.5 w-full border border-[#E4E7EA] rounded-xl p-2.5 text-sm focus:border-[#0D2F4D] outline-none"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">Next follow-up</label>
            <input
              value={terms.next_follow_up || ""}
              onChange={(ev) => setT({ next_follow_up: ev.target.value })}
              data-testid="employer-next-follow-up"
              placeholder="Fri 21st · or a specific date"
              className="mt-1.5 w-full border border-[#E4E7EA] rounded-xl p-2.5 text-sm focus:border-[#0D2F4D] outline-none"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">Feedback / observations</label>
            <textarea
              value={terms.feedback || ""}
              onChange={(ev) => setT({ feedback: ev.target.value })}
              data-testid="employer-feedback"
              rows={3}
              placeholder="Vibes, hiring-manager quirks, budget signals, deal-breakers…"
              className="mt-1.5 w-full border border-[#E4E7EA] rounded-xl p-3 text-sm focus:border-[#0D2F4D] outline-none"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">Recruiter notes</label>
            <textarea
              value={terms.recruiter_notes || ""}
              onChange={(ev) => setT({ recruiter_notes: ev.target.value })}
              data-testid="employer-recruiter-notes"
              rows={4}
              placeholder="Deal notes, negotiation history, internal reminders."
              className="mt-1.5 w-full border border-[#E4E7EA] rounded-xl p-3 text-sm focus:border-[#0D2F4D] outline-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
 * SubmissionsPanel — the Manual Submission Manager. A "Candidates Sent" list
 * with per-candidate status, editable notes, and full per-submission history.
 * ═══════════════════════════════════════════════════════════════════════ */
const SUBMISSION_STATUSES = ["sent", "interested", "interviewing", "hired", "rejected"];
const SUB_STATUS_STYLE = {
  sent:         "bg-slate-100 text-slate-700 border-slate-200",
  interested:   "bg-blue-50 text-blue-700 border-blue-200",
  interviewing: "bg-amber-50 text-amber-700 border-amber-200",
  hired:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected:     "bg-red-50 text-red-600 border-red-200",
};

function SubmissionsPanel({ employerId, pw }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("");
  const H = { headers: { "X-Admin-Password": pw } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/employers/${employerId}/submissions`, H);
      setSubs(r.data.submissions || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employerId, pw]);

  useEffect(() => { load(); }, [load]);

  const addSub = async () => {
    if (!newName.trim()) { toast.error("Enter the candidate's name."); return; }
    setAdding(true);
    try {
      await axios.post(`${BASE}/employers/${employerId}/submissions`,
        { candidate_name: newName, role_title: newRole, status: "sent" }, H);
      setNewName(""); setNewRole("");
      toast.success("Candidate added to the submission list.");
      load();
    } catch (e) { toast.error("Couldn't add candidate."); }
    finally { setAdding(false); }
  };

  return (
    <div className="space-y-4" data-testid="submissions-panel">
      <SectionHeader title="Candidates Sent" subtitle="Everyone you've submitted to this employer — track status, notes and history." />

      <div className="bg-[#0D2F4D] rounded-xl p-4 grid md:grid-cols-[2fr_2fr_auto] gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Candidate name"
          data-testid="submission-new-name"
          className="rounded-lg border border-white/20 bg-white/10 text-white placeholder:text-white/40 px-3 py-2 text-sm"
        />
        <input
          value={newRole}
          onChange={(e) => setNewRole(e.target.value)}
          placeholder="Role (optional)"
          data-testid="submission-new-role"
          className="rounded-lg border border-white/20 bg-white/10 text-white placeholder:text-white/40 px-3 py-2 text-sm"
        />
        <button
          onClick={addSub}
          disabled={adding}
          data-testid="submission-add-btn"
          className="px-4 py-2 text-xs font-bold rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#d4e02c] disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          <Plus size={13} /> {adding ? "Adding…" : "Add candidate"}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[#6F8FA8]">Loading…</p>
      ) : subs.length === 0 ? (
        <EmptyState title="No candidates sent yet" subtitle="Add the first candidate you've submitted to this employer above." />
      ) : (
        <div className="space-y-3">
          {subs.map((s) => (
            <SubmissionCard key={s.id} sub={s} pw={pw} onChange={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubmissionCard({ sub, pw, onChange }) {
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const H = { headers: { "X-Admin-Password": pw } };

  const patch = async (payload, msg) => {
    setSaving(true);
    try {
      await axios.patch(`${BASE}/submissions/${sub.id}`, payload, H);
      if (msg) toast.success(msg);
      if (payload.note) setNoteDraft("");
      onChange?.();
    } catch (e) { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${sub.candidate_name} from this employer's submission list?`)) return;
    try { await axios.delete(`${BASE}/submissions/${sub.id}`, H); onChange?.(); }
    catch (e) { toast.error("Delete failed."); }
  };

  return (
    <div className="bg-white rounded-xl border border-[#E4E7EA] p-4" data-testid={`submission-card-${sub.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-bold text-[#0D2F4D]">{sub.candidate_name}</p>
          {sub.role_title && <p className="text-xs text-[#6F8FA8]">for {sub.role_title}</p>}
          {sub.candidate_email && <p className="text-[11px] text-[#6F8FA8]">{sub.candidate_email}</p>}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sub.status}
            onChange={(e) => patch({ status: e.target.value }, `Moved to ${e.target.value}.`)}
            data-testid={`submission-status-${sub.id}`}
            className={cx("text-[11px] font-bold uppercase tracking-wide rounded-full border px-2.5 py-1 cursor-pointer", SUB_STATUS_STYLE[sub.status] || SUB_STATUS_STYLE.sent)}
          >
            {SUBMISSION_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
          </select>
          <button onClick={remove} data-testid={`submission-delete-${sub.id}`} className="text-[#B9C2CC] hover:text-red-500">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {sub.notes && (
        <p className="mt-3 text-sm text-[#33475F] bg-[#F7F8F5] rounded-lg px-3 py-2 whitespace-pre-wrap">{sub.notes}</p>
      )}

      <div className="mt-3 flex items-end gap-2">
        <textarea
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          rows={2}
          placeholder="Add a note (e.g. 'Employer loved the CV, call booked 10am')…"
          data-testid={`submission-note-input-${sub.id}`}
          className="flex-1 text-sm rounded-lg border border-[#D6DCE3] px-3 py-2 outline-none focus:border-[#0D2F4D] resize-y"
        />
        <button
          onClick={() => noteDraft.trim() && patch({ note: noteDraft }, "Note saved to history.")}
          disabled={saving || !noteDraft.trim()}
          data-testid={`submission-note-save-${sub.id}`}
          className="px-3 py-2 text-xs font-bold rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90 disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          <Save size={12} /> Save note
        </button>
      </div>

      {(sub.history || []).length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowHistory((v) => !v)}
            data-testid={`submission-history-toggle-${sub.id}`}
            className="text-[11px] font-bold uppercase tracking-widest text-[#6F8FA8] hover:text-[#0D2F4D] inline-flex items-center gap-1"
          >
            <Clock size={11} /> History ({sub.history.length}) {showHistory ? "▾" : "▸"}
          </button>
          {showHistory && (
            <ul className="mt-2 space-y-1.5 border-l-2 border-[#E4E7EA] pl-3">
              {[...sub.history].reverse().map((h, i) => (
                <li key={i} className="text-xs text-[#33475F]">
                  <span className="text-[#9AA7B2]">{relTime(h.at)}</span>{" "}
                  {h.type === "status" ? (
                    <span><b>{h.from || "—"}</b> → <b>{h.to}</b></span>
                  ) : h.type === "created" ? (
                    <span>Added to list{h.to ? ` as ${h.to}` : ""}</span>
                  ) : (
                    <span>“{h.note}”</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
